// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Where the camera goes, and why it never rotates.
 *
 * The whole render is an OFF-AXIS (frustum-shear) camera, never a look-at one.
 * With frustum shear the lateral parallax of a point is exactly
 *
 *     dx = f t (1/Zc - 1/Z)
 *
 * which is affine in inverse depth — and inverse depth is precisely what the
 * network hands us. So there is no projection matrix, no view matrix and no
 * perspective divide anywhere in the shader: each vertex is displaced in screen
 * space by `shift * (depth - pivot)` and that is the exact answer, not an
 * approximation. Checked numerically against the true projection over near:far
 * ratios of 3:1, 10:1 and 100:1 and translations up to 10% of the frame: the
 * largest disagreement was 5.6e-17, i.e. float round-off.
 *
 * A toe-in camera — rotating to look at a target, which is what anyone would
 * write first — is wrong twice in ways you can see. In the same experiment a
 * point sitting exactly ON the convergence plane still moved by 2.02 screen
 * units per unit of translation when it should not move at all, and off-centre
 * points picked up vertical shift (keystone). Vertical parallax is the classic
 * cause of eye strain in an anaglyph, and it makes the excursion cap's
 * arithmetic wrong, since the cap assumes displacement is proportional to depth
 * difference and nothing else.
 *
 * LOOPING. Every path here is 1-periodic in BOTH value and first derivative, so
 * a clip cut at phase 1 joins its own phase 0 with no kink. That is why the
 * dolly is a raised cosine rather than a triangle: a triangle wave loops in
 * value but reverses velocity instantly at each end, which reads as a jerk.
 */

export type PathName = 'sway' | 'orbit' | 'figure8' | 'dolly' | 'kenburns';

export const PATHS: readonly { id: PathName; label: string; hint: string }[] = [
  { id: 'sway', label: 'Sway', hint: 'left and right — the classic 3D-photo move' },
  { id: 'orbit', label: 'Orbit', hint: 'a slow circle around the subject' },
  { id: 'figure8', label: 'Figure of eight', hint: 'a wider wander, more depth on show' },
  { id: 'dolly', label: 'Push in', hint: 'straight towards the subject and back' },
  { id: 'kenburns', label: 'Ken Burns', hint: 'a drift and a push, with real depth' },
];

export interface CameraSample {
  /** Screen shift per unit of depth difference, as a fraction of frame width. */
  readonly tx: number;
  readonly ty: number;
  /** Scale about the frame centre. Always >= 1. */
  readonly zoom: number;
  /**
   * Depth-dependent scale — a real dolly, in the same fraction-of-frame-width
   * units as `tx`.
   *
   * A dolly is a move along the view axis, and its parallax is a change of
   * MAGNIFICATION with distance rather than a translation: near things grow
   * faster than far ones. Modelling it as a translation of zero, with only a
   * uniform zoom, is a mistake that is easy to make and produces a path that
   * renders an ordinary flat Ken Burns zoom with no depth effect whatsoever —
   * the one movement a person would pick to travel INTO the picture being the
   * one that does nothing. It also drives the excursion cap's arithmetic to
   * infinity, because a path with no excursion has nothing to bound.
   */
  readonly dz: number;
}

const TAU = Math.PI * 2;

/**
 * Sample a path.
 *
 * `phase` is in [0,1). `amp` is the peak one-sided excursion as a fraction of
 * frame width — half of the peak-to-peak figure `excursionCap` produces.
 * `push` is how much the dolly paths zoom, as a fraction (0.08 = 8%).
 */
export function evalPath(path: PathName, phase: number, amp: number, push = 0.08): CameraSample {
  const p = phase - Math.floor(phase);
  const a = TAU * p;
  // Raised cosine: 0 at p=0 and p=1, 1 at p=0.5, with zero derivative at both
  // ends — so the turnaround is invisible as well as continuous.
  const inOut = (1 - Math.cos(a)) / 2;
  switch (path) {
    case 'sway':
      return { tx: amp * Math.sin(a), ty: 0, zoom: 1, dz: 0 };
    case 'orbit':
      return { tx: amp * Math.cos(a), ty: amp * 0.6 * Math.sin(a), zoom: 1, dz: 0 };
    case 'figure8':
      return { tx: amp * Math.sin(a), ty: amp * 0.5 * Math.sin(2 * a), zoom: 1, dz: 0 };
    case 'dolly':
      return { tx: 0, ty: 0, zoom: 1 + push * inOut, dz: amp * inOut };
    case 'kenburns':
      return {
        tx: amp * 0.5 * Math.sin(a),
        ty: amp * 0.25 * Math.cos(a),
        zoom: 1 + push * inOut,
        dz: amp * 0.5 * inOut,
      };
    default: {
      const exhaustive: never = path;
      throw new Error(`unknown path ${String(exhaustive)}`);
    }
  }
}

/**
 * The largest displacement a path reaches on each axis.
 *
 * The depth-dependent zoom counts: at a corner of the frame it displaces a
 * vertex by `dz * (depth - pivot)`, which is exactly the same quantity a
 * lateral shift of `dz` would produce. So it belongs in the extent, and every
 * registered path therefore has a non-zero one — which is what keeps the
 * excursion cap from dividing by zero and silently going to its maximum.
 */
export function pathExtent(path: PathName, amp: number): { x: number; y: number } {
  switch (path) {
    case 'sway':
      return { x: amp, y: 0 };
    case 'orbit':
      return { x: amp, y: amp * 0.6 };
    case 'figure8':
      return { x: amp, y: amp * 0.5 };
    case 'dolly':
      return { x: amp, y: amp };
    case 'kenburns':
      return { x: amp * 0.5 + amp * 0.5, y: amp * 0.25 + amp * 0.5 };
    default: {
      const exhaustive: never = path;
      throw new Error(`unknown path ${String(exhaustive)}`);
    }
  }
}

/**
 * How much the sheet must be scaled up so a shifted frame never shows the void
 * beyond its own edge.
 *
 * A vertex's displacement is `t * (depth - pivot)` and `depth - pivot` is in
 * [-1, 1], so the extreme displacement is the path's extent. Covering it on
 * both sides needs twice that, and a little more so a rounding error at the
 * very edge does not put a one-pixel black line down the side of somebody's
 * exported clip.
 *
 * `aspect` is width over height. The vertical extent is carried in
 * fraction-of-WIDTH units like everything else, and normalised device
 * coordinates span 2 across each axis, so a vertical displacement is multiplied
 * by the aspect ratio on its way to the shader — and the overscan, which scales
 * both axes equally, has to cover the larger of the two.
 */
export function overscanFor(path: PathName, amp: number, aspect = 1): number {
  const e = pathExtent(path, amp);
  return 1 + 2 * Math.max(e.x, e.y * aspect) + 0.004;
}

/**
 * The phases of an N-frame loop.
 *
 * i/N, never i/(N-1): the latter puts phase 1 in the clip, which is the same
 * picture as phase 0, so the loop stutters on one duplicated frame every time
 * it wraps.
 */
export function loopPhases(frames: number): Float64Array {
  const out = new Float64Array(frames);
  for (let i = 0; i < frames; i += 1) out[i] = i / frames;
  return out;
}
